import React from 'react';
import type { SwitchProps } from './types';

/**
 * Each state's light and dark colours are taken from the design spec as independent, literal values
 * rather than letting the ramp's automatic light/dark mirror derive one from the other — most of these
 * pairs land off the mirror (e.g. "on" keeps a dark track in light mode and a white track in dark
 * mode, both read as the strong/filled state; "off" stays a muted, near-background track in both). A
 * dark: class is added only where the plain class's automatic mirror wouldn't already match the spec.
 */
const TRACK_ENABLED = {
    off: 'border-primary-600 dark:border-primary-700 bg-primary-50 cursor-pointer',
    on: 'border-primary-900 dark:border-primary-1250 bg-primary-900 dark:bg-primary-1250 cursor-pointer',
};

const TRACK_DISABLED = {
    off: 'border-primary-600 bg-primary-200 cursor-not-allowed',
    on: 'border-primary-600 bg-primary-600 cursor-not-allowed',
};

const KNOB_ENABLED = {
    off: 'bg-primary-900 dark:bg-primary-800',
    on: 'bg-primary-50',
};

const KNOB_DISABLED = {
    off: 'bg-primary-500',
    on: 'bg-primary-50 dark:bg-primary-400',
};

export const Switch: React.FC<SwitchProps> = ({ checked, onChange, disabled = false, label, testId }) => {
    const state = checked ? 'on' : 'off';
    const trackClass = disabled ? TRACK_DISABLED[state] : TRACK_ENABLED[state];
    const knobClass = disabled ? KNOB_DISABLED[state] : KNOB_ENABLED[state];

    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            data-testid={testId}
            disabled={disabled}
            onClick={() => onChange(!checked)}
            className={
                `relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border shadow-weak ` +
                `transition-colors duration-200 touch-target focus-visible:outline-none focus-visible:border-focus ` +
                `focus-visible:ring-4 focus-visible:ring-focus-ring ${trackClass}`
            }
        >
            <span
                className={
                    `absolute left-0.5 top-0.5 h-3.5 w-3.5 rounded-full transition-transform duration-200 ` +
                    `${checked ? 'translate-x-4' : 'translate-x-0'} ${knobClass}`
                }
            />
        </button>
    );
};
